import { forwardRef } from 'react';
import { Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import type { SecurityAssessmentResponse, DocumentAnalysisResponse } from '../types';

// Use local logo to avoid CORS issues with html2canvas
const PELLES_LOGO_LOCAL = '/pelles-logo.png';

const PellesLogo = () => (
  <Box
    component="img"
    src={PELLES_LOGO_LOCAL}
    alt="Pelles.AI"
    sx={{
      height: 40,
      width: 'auto',
    }}
  />
);

interface SecurityAssessmentReportProps {
  data: SecurityAssessmentResponse;
  csvAnalysis?: DocumentAnalysisResponse;
}

const SecurityAssessmentReport = forwardRef<HTMLDivElement, SecurityAssessmentReportProps>(
  ({ data, csvAnalysis }, ref) => {
    // Helper functions
    const getHealthColor = (status: string) => {
      switch (status) {
        case 'healthy':
          return '#22c55e';
        case 'moderate':
          return '#eab308';
        case 'at_risk':
          return '#f97316';
        case 'critical':
          return '#ef4444';
        default:
          return '#000000';
      }
    };

    const getHealthLabel = (status: string) => {
      switch (status) {
        case 'healthy':
          return 'Healthy';
        case 'moderate':
          return 'Moderate';
        case 'at_risk':
          return 'At Risk';
        case 'critical':
          return 'Critical';
        default:
          return status;
      }
    };

    const getSeverityColor = (severity: string) => {
      switch (severity) {
        case 'critical':
          return '#ef4444';
        case 'high':
          return '#f97316';
        case 'medium':
          return '#eab308';
        case 'low':
          return '#3b82f6';
        default:
          return '#64748b';
      }
    };

    const getCategoryColor = (percentage: number) => {
      if (percentage >= 90) return '#22c55e';
      if (percentage >= 70) return '#eab308';
      return '#ef4444';
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
      <Box
        ref={ref}
        sx={{
          width: 1200,
          minHeight: 1000,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: 4,
          fontFamily: 'Lato, sans-serif',
          color: '#000000', // ADHOC: Force default text color to black for PDF
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <PellesLogo />
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: '#000000' }}>
                Generated: {formatDate(data.generatedAt)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#000000' }} display="block">
                Period: {formatDate(data.periodStart)} - {formatDate(data.periodEnd)} ({data.periodDays} days)
              </Typography>
            </Box>
          </Box>
          <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
            Security Assessment Report
          </Typography>
          <Typography variant="h6" sx={{ color: '#000000' }}>
            {data.orgName}
          </Typography>
        </Box>

        {/* Overall Score Section */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#000000' }}>
            <SecurityIcon /> Overall Security Posture
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Score Display */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" fontWeight="bold" color={getHealthColor(data.healthStatus)}>
                {data.overallScore.toFixed(1)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                out of 100
              </Typography>
              <Chip
                label={getHealthLabel(data.healthStatus)}
                sx={{
                  mt: 1,
                  bgcolor: getHealthColor(data.healthStatus),
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </Box>

            {/* Risk Counts */}
            <Box sx={{ flex: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ bgcolor: '#fef2f2', p: 2, borderRadius: 1, flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="#ef4444" fontWeight="bold">
                  {data.criticalRiskCount}
                </Typography>
                <Typography variant="caption" sx={{ color: '#000000' }}>
                  Critical Risks
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff7ed', p: 2, borderRadius: 1, flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="#f97316" fontWeight="bold">
                  {data.highRiskCount}
                </Typography>
                <Typography variant="caption" sx={{ color: '#000000' }}>
                  High Risks
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#fef9c3', p: 2, borderRadius: 1, flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="#eab308" fontWeight="bold">
                  {data.mediumRiskCount}
                </Typography>
                <Typography variant="caption" sx={{ color: '#000000' }}>
                  Medium Risks
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#eff6ff', p: 2, borderRadius: 1, flex: 1, minWidth: 120 }}>
                <Typography variant="h4" color="#3b82f6" fontWeight="bold">
                  {data.lowRiskCount}
                </Typography>
                <Typography variant="caption" sx={{ color: '#000000' }}>
                  Low Risks
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Executive Summary Section */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#000000' }}>
            Executive Summary
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, whiteSpace: 'pre-line', lineHeight: 1.8, color: '#000000' }}
          >
            {data.executiveSummary.narrative}
          </Typography>

          {data.executiveSummary.keyStrengths.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="success.main" sx={{ mb: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Key Strengths
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {data.executiveSummary.keyStrengths.map((strength, index) => (
                  <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5, color: '#000000' }}>
                    {strength}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {data.executiveSummary.criticalConcerns.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="error.main" sx={{ mb: 1 }}>
                <ErrorIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Critical Concerns
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {data.executiveSummary.criticalConcerns.map((concern, index) => (
                  <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5, color: '#000000' }}>
                    {concern}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {data.executiveSummary.highConcerns.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="warning.main" sx={{ mb: 1 }}>
                <WarningIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                High Priority Concerns
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {data.executiveSummary.highConcerns.map((concern, index) => (
                  <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5, color: '#000000' }}>
                    {concern}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {data.executiveSummary.weakCategories.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#000000', mb: 1 }}>
                <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Areas Requiring Attention
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {data.executiveSummary.weakCategories.map((category, index) => (
                  <Chip key={index} label={category} size="small" variant="outlined" sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Category Scores Section */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            pageBreakInside: 'avoid',
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#000000' }}>
            Security Category Scores
          </Typography>

          {/* Bar Chart */}
          <Box sx={{ mb: 3, height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.categoryScores.map((score) => ({
                  name: score.categoryName,
                  percentage: Number(((score.score / score.maxScore) * 100).toFixed(1)),
                }))}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={100} />
                <Bar dataKey="percentage" radius={[0, 8, 8, 0]}>
                  {data.categoryScores.map((score, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getCategoryColor((score.score / score.maxScore) * 100)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Detailed Breakdown */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#000000' }}><strong>Category</strong></TableCell>
                  <TableCell align="right" sx={{ color: '#000000' }}><strong>Score</strong></TableCell>
                  <TableCell sx={{ color: '#000000' }}><strong>Issues Found</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.categoryScores.map((score, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: '#000000' }}>{score.categoryName}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${((score.score / score.maxScore) * 100).toFixed(1)}%`}
                        size="small"
                        sx={{
                          bgcolor: getCategoryColor((score.score / score.maxScore) * 100),
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#000000' }}>
                      {score.issuesFound.length > 0 ? (
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {score.issuesFound.map((issue, idx) => (
                            <Typography component="li" key={idx} variant="caption" sx={{ color: '#000000' }}>
                              {issue}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#000000' }}>
                          No issues detected
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Identified Risks Section */}
        {data.risks.length > 0 && (
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#000000' }}>
              Identified Security Risks
            </Typography>
            {data.risks.map((risk, index) => (
              <Box key={index} sx={{ mb: 3, pb: 3, borderBottom: index < data.risks.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label={risk.severity.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: getSeverityColor(risk.severity),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                  <Chip
                    label={`Priority: ${risk.priority}`}
                    size="small"
                    variant="outlined"
                    sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }}
                  />
                  <Typography variant="body2" sx={{ color: '#000000' }}>
                    {risk.category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                  {risk.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#000000', mb: 1 }}>
                  {risk.description}
                </Typography>
                {risk.affectedResources.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: '#000000' }}>
                      Affected ({risk.affectedCount}):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {risk.affectedResources.slice(0, 10).map((resource, idx) => (
                        <Chip key={idx} label={resource} size="small" variant="outlined" sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }} />
                      ))}
                      {risk.affectedCount > 10 && (
                        <Chip label={`+${risk.affectedCount - 10} more`} size="small" variant="outlined" sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }} />
                      )}
                    </Box>
                  </Box>
                )}
                {risk.remediationSteps.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: '#000000' }}>
                      Remediation Steps:
                    </Typography>
                    <Box component="ol" sx={{ m: 0, pl: 2 }}>
                      {risk.remediationSteps.map((step, idx) => (
                        <Typography component="li" key={idx} variant="caption" sx={{ color: '#000000' }}>
                          {step}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Security Metrics Section */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 3,
            mb: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#000000' }}>
            Security Metrics
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {/* Authentication Metrics */}
            <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                Authentication
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ color: '#000000' }}>Failed Logins: {data.metrics.failedLoginAttempts}</Typography>
                <Typography variant="body2" sx={{ color: '#000000' }}>Off-Hours Logins: {data.metrics.offHoursLogins}</Typography>
                <Typography variant="body2" sx={{ color: '#000000' }}>Unusual Locations: {data.metrics.unusualLocations}</Typography>
              </Box>
            </Box>

            {/* Access Control Metrics */}
            <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                Access Control
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ color: '#000000' }}>Privileged Users: {data.metrics.privilegedUsers}</Typography>
                <Typography variant="body2" sx={{ color: '#000000' }}>Permission Escalations: {data.metrics.permissionEscalations}</Typography>
              </Box>
            </Box>

            {/* Data Security Metrics */}
            <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                Data Security
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ color: '#000000' }}>Medium Bulk Ops (10-49): {data.metrics.bulkOperationsMedium}</Typography>
                <Typography variant="body2" sx={{ color: '#000000' }}>Large Bulk Ops (50+): {data.metrics.bulkOperationsLarge}</Typography>
                <Typography variant="body2" sx={{ color: '#000000' }}>External Shares: {data.metrics.externalShares}</Typography>
              </Box>
            </Box>

            {/* User Behavior Metrics */}
            <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                User Behavior
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ color: '#000000' }}>High-Access Users: {data.metrics.highAccessUsers}</Typography>
                <Typography variant="body2" sx={{ color: '#000000' }}>Suspicious Activity: {data.metrics.suspiciousActivityCount}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Sensitive Content Analysis Section */}
        {data.sensitiveRequests.totalRequestsAnalyzed > 0 && (
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#000000' }}>
              Sensitive Content Analysis
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                Analyzed {data.sensitiveRequests.totalRequestsAnalyzed} requests and detected{' '}
                {data.sensitiveRequests.totalSensitiveRequests} sensitive requests from{' '}
                {data.sensitiveRequests.affectedUsers} unique users.
              </Typography>
            </Box>

            {/* Breakdown by Severity */}
            {Object.keys(data.sensitiveRequests.bySeverity).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                  By Severity
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(data.sensitiveRequests.bySeverity).map(([severity, count]) => (
                    <Chip
                      key={severity}
                      label={`${severity}: ${count}`}
                      size="small"
                      sx={{
                        bgcolor: getSeverityColor(severity),
                        color: 'white',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Breakdown by Content Type */}
            {Object.keys(data.sensitiveRequests.bySensitivityType).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                  By Content Type
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(data.sensitiveRequests.bySensitivityType).map(([type, count]) => (
                    <Chip key={type} label={`${type}: ${count}`} size="small" variant="outlined" sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Examples */}
            {data.sensitiveRequests.examples.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                  Top Examples
                </Typography>
                {data.sensitiveRequests.examples.map((example, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      pb: 2,
                      borderBottom: index < data.sensitiveRequests.examples.length - 1 ? '1px solid #e2e8f0' : 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                      <Chip
                        label={example.sensitivityLevel.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: getSeverityColor(example.sensitivityLevel),
                          color: 'white',
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#000000' }}>
                        {example.userName} • {formatDate(example.timestamp)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      {example.detectedTypes.map((type, idx) => (
                        <Chip key={idx} label={type} size="small" variant="outlined" sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }} />
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ color: '#000000', display: 'block', mb: 0.5 }}>
                      {example.explanation}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        bgcolor: '#f8fafc',
                        p: 1,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        color: '#000000',
                      }}
                    >
                      {example.requestPreview}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Document Sensitivity Analysis Section */}
        {csvAnalysis && csvAnalysis.totalDocuments > 0 && (
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#000000' }}>
              <UploadFileIcon /> Document Sensitivity Analysis
            </Typography>

            {/* Overview */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#000000' }}>
                Analyzed {csvAnalysis.totalDocuments} document summaries and identified{' '}
                <strong>
                  {csvAnalysis.sensitiveDocuments} sensitive documents (
                  {csvAnalysis.totalDocuments > 0
                    ? ((csvAnalysis.sensitiveDocuments / csvAnalysis.totalDocuments) * 100).toFixed(1)
                    : 0}
                  %)
                </strong>
                .
              </Typography>
            </Box>

            {csvAnalysis.sensitiveDocuments > 0 ? (
              <>
                {/* Breakdown by Severity */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                    By Severity
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {csvAnalysis.criticalCount > 0 && (
                      <Chip
                        label={`Critical: ${csvAnalysis.criticalCount}`}
                        size="small"
                        sx={{ bgcolor: '#ef4444', color: 'white' }}
                      />
                    )}
                    {csvAnalysis.highCount > 0 && (
                      <Chip
                        label={`High: ${csvAnalysis.highCount}`}
                        size="small"
                        sx={{ bgcolor: '#f97316', color: 'white' }}
                      />
                    )}
                    {csvAnalysis.mediumCount > 0 && (
                      <Chip
                        label={`Medium: ${csvAnalysis.mediumCount}`}
                        size="small"
                        sx={{ bgcolor: '#eab308', color: 'white' }}
                      />
                    )}
                    {csvAnalysis.lowCount > 0 && (
                      <Chip
                        label={`Low: ${csvAnalysis.lowCount}`}
                        size="small"
                        sx={{ bgcolor: '#3b82f6', color: 'white' }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Breakdown by Type */}
                {Object.keys(csvAnalysis.bySensitivityType).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                      By Content Type
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Object.entries(csvAnalysis.bySensitivityType)
                        .sort(([, a], [, b]) => b - a)
                        .map(([type, count]) => (
                          <Chip
                            key={type}
                            label={`${type}: ${count}`}
                            size="small"
                            variant="outlined"
                            sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }}
                          />
                        ))}
                    </Box>
                  </Box>
                )}

                {/* Top Flagged Documents */}
                {csvAnalysis.flaggedDocuments.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#000000' }}>
                      Top Flagged Documents (showing up to 5)
                    </Typography>
                    {csvAnalysis.flaggedDocuments.slice(0, 5).map((doc, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          pb: 2,
                          borderBottom:
                            index < Math.min(csvAnalysis.flaggedDocuments.length, 5) - 1
                              ? '1px solid #e2e8f0'
                              : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={doc.sensitivityLevel.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: getSeverityColor(doc.sensitivityLevel),
                              color: 'white',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                          {doc.detectedTypes.map((type, idx) => (
                            <Chip
                              key={idx}
                              label={type}
                              size="small"
                              variant="outlined"
                              sx={{ color: '#000000', borderColor: 'rgba(0,0,0,0.23)' }}
                            />
                          ))}
                        </Box>
                        <Typography variant="caption" sx={{ color: '#000000', display: 'block', mb: 0.5 }}>
                          <strong>Risk:</strong> {doc.riskExplanation}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#000000', display: 'block', mb: 0.5 }}>
                          <strong>Action:</strong> {doc.recommendedAction}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            bgcolor: '#f8fafc',
                            p: 1,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            color: '#000000',
                          }}
                        >
                          {doc.documentSummary}
                        </Typography>
                      </Box>
                    ))}
                    {csvAnalysis.flaggedDocuments.length > 5 && (
                      <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                        ... and {csvAnalysis.flaggedDocuments.length - 5} more flagged documents
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #86efac' }}>
                <Typography variant="body2" sx={{ color: '#15803d' }}>
                  ✓ No sensitive content detected in uploaded documents.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Remediation Plan Section */}
        {data.remediationPlan.length > 0 && (
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#000000' }}>
              Remediation Action Plan
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#000000' }}><strong>Priority</strong></TableCell>
                    <TableCell sx={{ color: '#000000' }}><strong>Category</strong></TableCell>
                    <TableCell sx={{ color: '#000000' }}><strong>Risks</strong></TableCell>
                    <TableCell sx={{ color: '#000000' }}><strong>Effort</strong></TableCell>
                    <TableCell sx={{ color: '#000000' }}><strong>Time</strong></TableCell>
                    <TableCell sx={{ color: '#000000' }}><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.remediationPlan.map((action, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ color: '#000000' }}>
                        <Chip label={`#${action.priorityOrder}`} size="small" color="primary" />
                      </TableCell>
                      <TableCell sx={{ color: '#000000' }}>{action.categoryName}</TableCell>
                      <TableCell sx={{ color: '#000000' }}>
                        <Chip label={action.riskCount} size="small" />
                      </TableCell>
                      <TableCell sx={{ color: '#000000' }}>{action.effortLevel}</TableCell>
                      <TableCell sx={{ color: '#000000' }}>{action.estimatedTime}</TableCell>
                      <TableCell sx={{ color: '#000000' }}>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {action.actions.slice(0, 3).map((actionItem, idx) => (
                            <Typography component="li" key={idx} variant="caption" sx={{ color: '#000000' }}>
                              {actionItem}
                            </Typography>
                          ))}
                          {action.actions.length > 3 && (
                            <Typography variant="caption" sx={{ color: '#000000' }}>
                              ...and {action.actions.length - 3} more
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            textAlign: 'center',
            pt: 3,
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <Typography variant="caption" sx={{ color: '#000000' }}>
            Generated by Pelles.ai Security Assessment Tool
          </Typography>
          <Typography variant="caption" sx={{ color: '#000000' }} display="block">
            {formatDate(data.generatedAt)}
          </Typography>
        </Box>
      </Box>
    );
  }
);

SecurityAssessmentReport.displayName = 'SecurityAssessmentReport';

export default SecurityAssessmentReport;
